// src/employee/pages/EmployeeChatbox.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { format, isToday, isYesterday, startOfDay } from "date-fns";

const formatDateHeader = (date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMM d");
};

export default function EmployeeChatbox({ isOpen, onClose }) {
  const { user } = useEmployeeAuth();
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Find HR email (assume first non-employee or known HR)
  const hrEmail = "kanishka@driveeasy.co.in"; // CHANGE TO YOUR HR EMAIL

  const chatId = `${hrEmail}_${user.email}`;
  const chatRef = doc(db, "hrChats", chatId);

  // Listen to chat
  useEffect(() => {
    const unsub = onSnapshot(chatRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setChat(data.messages || []);
        // Mark as read
        updateDoc(chatRef, { [`unread.${user.email}`]: false });
      }
      // Inside onSnapshot
      if (snap.exists()) {
        const data = snap.data();
        setChat(data.messages || []);

        // MARK AS READ WHEN OPENED
        if (data.unread?.[user.email]) {
          updateDoc(chatRef, { [`unread.${user.email}`]: false });
        }
      }
    });
    return unsub;
  }, [user.email]);

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setSending(true);

    const newMsg = {
      sender: user.email,
      message: msg,
      timestamp: new Date().toISOString(),
    };

    try {
      await setDoc(chatRef, {
        participants: [hrEmail, user.email],
        messages: arrayUnion(newMsg),
        lastMessageAt: serverTimestamp(),
        unread: { [hrEmail]: true }
      }, { merge: true });

      await updateDoc(chatRef, { [`unread.${user.email}`]: false });
    } catch (err) {
      alert("Failed");
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const groups = {};
  chat.forEach(m => {
    const d = startOfDay(new Date(m.timestamp)).toISOString();
    if (!groups[d]) groups[d] = [];
    groups[d].push(m);
  });

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ x: 400 }}
      animate={{ x: 0 }}
      exit={{ x: 400 }}
      className="fixed right-0 top-0 h-full w-96 bg-gradient-to-br from-neutral-900 to-indigo-950 shadow-2xl z-50 flex flex-col"
    >
      <div className="p-6 border-b border-white/20 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">HR Chat</h3>
          <p className="text-white/60 text-xs">Direct line to HR</p>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white">
          <FaTimes />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-6">
        {Object.entries(groups).map(([date, msgs]) => (
          <div key={date}>
            <div className="text-center text-white/50 text-xs mb-4">
              {formatDateHeader(new Date(date))}
            </div>
            {msgs.map((m, i) => {
              const isMe = m.sender === user.email;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-xs px-4 py-2 rounded-2xl text-sm ${
                    isMe ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white" :
                    "bg-white/10 text-white border border-white/20"
                  }`}>
                    <p>{m.message}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-white/20">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === "Enter" && send()}
            placeholder="Message HR..."
            className="flex-1 bg-white/10 border border-white/20 rounded-3xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 p-3 rounded-3xl hover:scale-110 transition disabled:opacity-50"
          >
            <FaPaperPlane className="text-white" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}